import React from 'react'
import { useSelector } from 'react-redux'
import { EllipsisReduxStore } from '~/types'

// moock usestore
export enum Statuses {
  Loading = 'Loading',
  Timeout = 'Timeout',
  Ready = 'Ready',
}

interface MessageData {
  action: string
  source: string
  data: unknown
  isAnsRequired: boolean
}

interface Actions {
  actionReady: string
  actionData: string
  actionCancel: string
}

interface UseEmbedParameters extends Actions {
  baseUrl: string
  data?: unknown
  timeoutMs: number
  source: string
  iframeElem: React.RefObject<HTMLIFrameElement>
}

const defaults: Partial<UseEmbedParameters> & Actions = {
  timeoutMs: 8000,
  source: 'custom_embed',
  actionReady: 'ready',
  actionData: 'data',
  actionCancel: 'cancel',
}

const generateUniqueId = () =>
  `${(+new Date()).toString(36).substr(-5)}${Math.random()
    .toString(36)
    .substr(2)}`

export function useEmbed(parameters: Partial<UseEmbedParameters> = defaults) {
  const parametersWithDefaults = {
    ...defaults,
    ...parameters,
  }
  const storyAnsData = useSelector(
    (state: EllipsisReduxStore) =>
      state.storyData.story || {
        headlines: {},
        subheadlines: {},
        taxonomy: {},
      }
  )

  const {
    timeoutMs,
    source,
    actionReady,
    actionData,
    actionCancel,
    baseUrl = '',
    data,
    iframeElem,
  } = parametersWithDefaults

  const uniqueIdRef = React.useRef<string>()
  if (!uniqueIdRef.current) {
    uniqueIdRef.current = generateUniqueId()
  }

  const urlObj = new URL(baseUrl)
  const searchParams = new URLSearchParams(urlObj.search)
  if (data) {
    searchParams.append('p', JSON.stringify(data))
  }
  searchParams.append('k', uniqueIdRef.current)

  urlObj.search = searchParams.toString()

  // toJSON handles URI encoding
  const fullUri = urlObj.toJSON()

  const [url, setUrl] = React.useState<string>(fullUri)
  const [messageData, setMessageData] = React.useState<MessageData>()
  const [status, setStatus] = React.useState<Statuses>(Statuses.Loading)
  const timeoutRef = React.useRef<number>()
  const { isAnsRequired = false } = messageData || {}

  // A URL change should reset the status
  // Calling retry() from outside does not work since new URL should
  // be returned after status is reset to avoid race conditions
  React.useEffect(() => {
    if (url !== fullUri) {
      // Set new URL so the following invocation will return back proper URL
      // We can't return fullUri right now. It should be available along with state==Statuses.Loading
      setUrl(fullUri)
      // Reset stale data
      setMessageData(undefined)
      // Restart
      setStatus(Statuses.Loading)
    }
  }, [url, fullUri])

  const handleMessage = React.useCallback(
    (event: MessageEvent) => {
      let messageData
      try {
        messageData = JSON.parse(event.data)
      } catch {
        return
      }

      if (
        !messageData ||
        messageData.source !== source ||
        (messageData.key && messageData.key !== uniqueIdRef.current)
      ) {
        return
      }

      if (status === Statuses.Loading && messageData.action === actionReady) {
        setStatus(Statuses.Ready)
        clearTimeout(timeoutRef.current)
        if (messageData.data) {
          setMessageData(messageData)
        }
      } else if (status === Statuses.Ready) {
        if (
          messageData.action !== actionData &&
          messageData.action !== actionCancel
        ) {
          return
        }
        setMessageData(messageData)
      }
    },
    [source, status, actionReady, actionData, actionCancel]
  )

  const unsubscribe = React.useCallback(() => {
    window.removeEventListener('message', handleMessage)
    clearTimeout(timeoutRef.current)
  }, [handleMessage])

  // Check timeout
  React.useEffect(() => {
    if (status === Statuses.Loading) {
      timeoutRef.current = window.setTimeout(() => {
        setStatus(Statuses.Timeout)
      }, timeoutMs)
    }
  }, [status, timeoutMs])

  React.useEffect(() => {
    window.addEventListener('message', handleMessage)
    return unsubscribe
  }, [handleMessage, unsubscribe])

  // PostMessage additional ANS to iframe
  React.useEffect(() => {
    if (!isAnsRequired) {
      return
    }
    // PostMessage additional ANS to iframe
    if (iframeElem && iframeElem.current && iframeElem.current.contentWindow) {
      const baseIframeSrc = new URL(iframeElem.current.src)
      iframeElem.current.contentWindow.postMessage(
        JSON.stringify({
          message: 'ans_data',
          data: {
            headlines: storyAnsData.headlines,
            subheadlines: storyAnsData.subheadlines,
            taxonomy: storyAnsData.taxonomy,
          },
        }),
        `${baseIframeSrc.protocol}//${baseIframeSrc.host}`
      )
    }
  }, [iframeElem, isAnsRequired, storyAnsData])

  return {
    messageData,
    retry: () => setStatus(Statuses.Loading),
    status,
    unsubscribe,
    url,
    actionReady,
    actionData,
    actionCancel,
  }
}
